import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import IconLoader from '../../icons/loader';
import IconDown from '../../icons/down';
import IconUp from '../../icons/up';
import IconChecked from '../../icons/checked';
import IconChangeLoClock from '../../icons/change-log-clock';
import IconExpand from '../../icons/expand';
import Select from '../../select/select';
import ButtonPrimary from '../../button/button-primary';
import { ButtonSize } from '../../button/types';
import Textarea from '../../textarea/textarea';
import RequestBlock from './request-block';
import { TransactionWarningActionEnum, TransactionTypeEnum } from '../types';
import getWarningActionName from './get-warning-action-name';
import formatDate from '../../../helpers/format-date';
import formatBalance from '../../../helpers/format-balance';
import { PermissionEnum } from '../../../base-types';
import { TransactionMonitoringRuleWarningTypes } from './types';
import IconRefresh from '../../icons/refresh';
import ButtonSecondary from '../../button/button-secondary';
import IconArrowRight from '../../icons/arrow-right';
import IconAnonymized from '../../icons/anonymized';
import Popup from '../../popup/popup';

import styles from '../transaction.module.css';
import ButtonPseudo from '../../button/button-pseudo';

const getTransactionTypesLink = (type: TransactionTypeEnum) => {
  let link;

  switch (type) {
    case TransactionTypeEnum.CURRENCY_EXCHANGE:
      link = '/transactions/exchange';
      break;
    case TransactionTypeEnum.GENERAL_LEDGER:
      link = '/transactions/general-ledger';
      break;
    case TransactionTypeEnum.INCOMING:
      link = '/transactions/incoming';
      break;
    case TransactionTypeEnum.INTERNAL:
      link = '/transactions/internal';
      break;
    case TransactionTypeEnum.NOSTRO:
      link = '/transactions/nostro';
      break;
    case TransactionTypeEnum.OUTGOING:
      link = '/transactions/outgoing';
      break;
    default:
      break;
  }

  return link;
};

export default function TransactionMonitoringRuleWarning({
  warning,
  employee,
  onApplyTransactionAction,
  transactionsTypes,
  applyTransactionsWarningIsLoading,
  internal,
  companyOfficials,
  recipients,
  senders,
  individualCustomer,
  recipientAccount,
  senderAccount,
  chats,
  createChatWithMessage,
  chatCreateInProgress,
  activePermissions,
  onResetTransactionWarningAction,
  resetTransactionsWarningIsSuccess,
  warningInGroupId,
  resetTransactionsWarningIsLoading,
  hideApproveActions,
  hideRejectActions,
  globalCollapsed,
  setHideWarningsIds,
  hideWarningsIds,
  isPaymentOrder,
  withWhiteBackground,
  getWarningGroupBeneficiaries,
  groupId,
  beneficiariesIsLoading,
  warningsReport,
  getWarningById,
  isWarningByIdFetching,
  isWarningByIdLoading,
  entityId,
  selectedWarningId,
  warningActionError,
}: TransactionMonitoringRuleWarningTypes) {
  const [collapsed, setCollapsed] = useState(false);
  const [action, setAction] = useState('');
  const [selectedRequest, setSelectedRequest] = useState('');
  const [requestText, setRequestText] = useState('');
  const [flagText, setFlagText] = useState('');
  const [resetPopupVisible, setResetPopupVisible] = useState(false);

  const onApply = () => {
    if (onApplyTransactionAction) {
      onApplyTransactionAction({
        body: {
          action,
          comment: flagText,
          employeeId: employee?.id as string,
        },
        warningId: warning?.id as string,
      });
    }
  };

  useEffect(() => {
    if (isPaymentOrder) {
      setCollapsed(!globalCollapsed);
    }
  }, [globalCollapsed, isPaymentOrder]);

  useEffect(() => {
    if (
      resetTransactionsWarningIsSuccess &&
      warningInGroupId &&
      warningInGroupId === warning?.id
    ) {
      setResetPopupVisible(false);
      setAction('');
      setFlagText('');
    }
  }, [warningInGroupId, resetTransactionsWarningIsSuccess]);

  useEffect(() => {
    if (warning?.action) {
      setAction(warning.action);
    }
  }, [warning?.action]);

  useEffect(() => {
    if (warning?.comment) {
      setFlagText(warning.comment);
    }
  }, [warning?.comment]);

  let warningIcon = <div className={styles.circle} />;

  if (warning?.action) {
    warningIcon = (
      <div className={styles.checked}>
        <IconChecked />
      </div>
    );

    if (warning.action === 'STOP') {
      warningIcon = (
        <div className={styles.declined}>
          <IconExpand />
        </div>
      );
    }
  }

  let collapsedContent;

  if (
    (isWarningByIdFetching || isWarningByIdLoading) &&
    selectedWarningId === warning?.id
  ) {
    collapsedContent = (
      <div className={styles.warningLoader}>
        <IconLoader />
      </div>
    );
  } else {
    collapsedContent = (
      <>
        <NavLink
          className={styles.monitoringRuleLink}
          to={`/transaction-monitoring-rules/${warning?.monitoringRuleId}`}
          target="_blank"
        >
          Go to rule
        </NavLink>
        {warning?.relatedTransactions &&
          warning.relatedTransactions.length > 0 && (
            <>
              <div className={styles.relatedTransactionTitle}>
                Related Transactions
              </div>
              {warning.relatedTransactions.map((item) => {
                const currentTransactionType = transactionsTypes?.find(
                  (type) => type.id === item.typeId,
                );

                return (
                  <div className={styles.relatedTransaction} key={item.id}>
                    <div className={styles.relatedTransactionLeft}>
                      <div className={styles.relatedTransactionDate}>
                        {formatDate(item.transferDate, true)}
                      </div>
                      <div className={styles.relatedTransactionBalance}>
                        {`${formatBalance(item.sum)} ${item.currency.name}`}
                      </div>
                    </div>
                    {item.typeId && (
                      <NavLink
                        className={styles.transactionLink}
                        target="_blank"
                        to={`${
                          getTransactionTypesLink(
                            currentTransactionType?.name as TransactionTypeEnum,
                          ) as string
                        }/${item.id}`}
                      >
                        Details
                      </NavLink>
                    )}
                  </div>
                );
              })}
            </>
          )}
        {warning &&
          ((warning?.warningsToPaymentOrderBeneficiaries?.edges &&
            warning?.warningsToPaymentOrderBeneficiaries?.edges?.length > 0) ||
            warning?.paymentOrderBeneficiary) && (
            <>
              <div className={styles.relatedTransactionTitle}>
                Orders
                {warning?.warningsToPaymentOrderBeneficiaries?.edges &&
                  warning?.warningsToPaymentOrderBeneficiaries?.edges?.length >
                    0 && (
                    <span
                      className={styles.ordersCount}
                    >{`${warning?.warningsToPaymentOrderBeneficiaries?.edges?.length} of ${warning?.warningsToPaymentOrderBeneficiaries?.pageInfo.totalCount}`}</span>
                  )}
              </div>
              {warning?.paymentOrderBeneficiary && (
                <>
                  <div className={styles.ordersTransaction}>
                    <div className={styles.ordersTransactionLeft}>
                      <div
                        className={`${styles.relatedTransactionDate} ${styles.ordersAmount}`}
                      >
                        <div>
                          <span>Sender Name: </span>
                          {warning?.paymentOrderBeneficiary.senderName ===
                          '%Anonymized%' ? (
                            <IconAnonymized
                              width={35}
                              height={12}
                              className={styles.anonymizedIcon}
                            />
                          ) : (
                            warning?.paymentOrderBeneficiary.senderName
                          )}
                          ;
                        </div>
                        <div>
                          <span>Local Amount: </span>
                          {`${formatBalance(
                            warning?.paymentOrderBeneficiary.localAmount,
                            warning?.paymentOrderBeneficiary.localCurrency,
                          )} ${
                            warning?.paymentOrderBeneficiary.localCurrency.name
                          }`}
                        </div>
                      </div>
                      <div
                        className={`${styles.relatedTransactionDate} ${styles.ordersAmount} ${styles.relatedTransactionLeftBottom}`}
                      >
                        {warning?.paymentOrderBeneficiary?.paymentOrder
                          ?.recepientAccount?.organization && (
                          <div>
                            <span>Beneficiary Company: </span>
                            <NavLink
                              className={styles.beneficiaryCompanyLink}
                              to={`/customers/${
                                warning?.paymentOrderBeneficiary?.paymentOrder
                                  ?.recepientAccount?.organization?.type ===
                                'CORPORATE'
                                  ? 'corporate'
                                  : 'individual'
                              }/${
                                warning?.paymentOrderBeneficiary?.paymentOrder
                                  ?.recepientAccount?.organization?.id
                              }`}
                              target="_blank"
                            >
                              {warning?.paymentOrderBeneficiary?.paymentOrder
                                ?.recepientAccount?.organization?.companyName ||
                                `${warning?.paymentOrderBeneficiary?.paymentOrder?.recepientAccount?.organization?.firstName} ${warning?.paymentOrderBeneficiary?.paymentOrder?.recepientAccount?.organization?.lastName}`}
                              ;
                            </NavLink>
                          </div>
                        )}
                        <div>
                          <span>Created Date: </span>
                          {formatDate(
                            warning?.paymentOrderBeneficiary.createdAt,
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.ordersTransactionControls}>
                      <NavLink
                        className={`${styles.transactionLink} ${styles.ordersTransactionControlsLink}`}
                        target="_blank"
                        to={`/transactions/cash-collection/list/${warning?.paymentOrderBeneficiary.paymentOrderId}`}
                      >
                        Cash Collection
                        <IconArrowRight width={11} height={11} />
                      </NavLink>
                      <NavLink
                        className={`${styles.transactionLink} ${styles.ordersTransactionControlsLink}`}
                        target="_blank"
                        to={`/transactions/cash-collection/orders/beneficiary/${warning?.paymentOrderBeneficiary.id}`}
                      >
                        Details
                        <IconArrowRight width={11} height={11} />
                      </NavLink>
                    </div>
                  </div>
                  {warning?.warningsToPaymentOrderBeneficiaries?.edges &&
                    warning?.warningsToPaymentOrderBeneficiaries?.edges
                      ?.length > 0 && (
                      <div className={styles.paymentOrderSeparator} />
                    )}
                </>
              )}
              <div className={styles.paymentOrdersBeneficiariesList}>
                {warning?.warningsToPaymentOrderBeneficiaries?.edges
                  ?.map((item) => item.node)
                  ?.map((item) => (
                    <div className={styles.ordersTransaction} key={item.id}>
                      <div className={styles.ordersTransactionLeft}>
                        <div
                          className={`${styles.relatedTransactionDate} ${styles.ordersAmount}`}
                        >
                          <div>
                            <span>Sender Name: </span>
                            {item.paymentOrderBeneficiary.senderName ===
                            '%Anonymized%' ? (
                              <IconAnonymized
                                width={35}
                                height={12}
                                className={styles.anonymizedIcon}
                              />
                            ) : (
                              item.paymentOrderBeneficiary.senderName
                            )}
                            ;
                          </div>
                          <div>
                            <span>Local Amount: </span>
                            {`${formatBalance(
                              item.paymentOrderBeneficiary.localAmount,
                              item.paymentOrderBeneficiary.localCurrency,
                            )} ${
                              item.paymentOrderBeneficiary.localCurrency.name
                            }`}
                          </div>
                        </div>
                        <div
                          className={`${styles.relatedTransactionDate} ${styles.ordersAmount} ${styles.relatedTransactionLeftBottom}`}
                        >
                          {item?.paymentOrderBeneficiary?.paymentOrder
                            ?.recepientAccount?.organization && (
                            <div>
                              <span>Beneficiary Company: </span>
                              <NavLink
                                className={styles.beneficiaryCompanyLink}
                                to={`/customers/${
                                  item?.paymentOrderBeneficiary?.paymentOrder
                                    ?.recepientAccount?.organization?.type ===
                                  'CORPORATE'
                                    ? 'corporate'
                                    : 'individual'
                                }/${
                                  item?.paymentOrderBeneficiary?.paymentOrder
                                    ?.recepientAccount?.organization?.id
                                }`}
                                target="_blank"
                              >
                                {item?.paymentOrderBeneficiary?.paymentOrder
                                  ?.recepientAccount?.organization
                                  ?.companyName ||
                                  `${item?.paymentOrderBeneficiary?.paymentOrder?.recepientAccount?.organization?.firstName} ${item?.paymentOrderBeneficiary?.paymentOrder?.recepientAccount?.organization?.lastName}`}
                                ;
                              </NavLink>
                            </div>
                          )}
                          <div>
                            <span>Created Date: </span>
                            {formatDate(
                              item?.paymentOrderBeneficiary?.createdAt,
                            )}
                          </div>
                        </div>
                      </div>
                      <div className={styles.ordersTransactionControls}>
                        <NavLink
                          className={`${styles.transactionLink} ${styles.ordersTransactionControlsLink}`}
                          target="_blank"
                          to={`/transactions/cash-collection/list/${item?.paymentOrderBeneficiary.paymentOrderId}`}
                        >
                          Cash Collection
                          <IconArrowRight width={11} height={11} />
                        </NavLink>
                        <NavLink
                          className={`${styles.transactionLink} ${styles.ordersTransactionControlsLink}`}
                          target="_blank"
                          to={`/transactions/cash-collection/orders/beneficiary/${item.id}`}
                        >
                          Details
                          <IconArrowRight width={11} height={11} />
                        </NavLink>
                      </div>
                    </div>
                  ))}
              </div>
              {warning?.warningsToPaymentOrderBeneficiaries?.pageInfo
                ?.hasNextPage &&
                groupId &&
                getWarningGroupBeneficiaries && (
                  <div className={styles.showMoreBeneficiaries}>
                    <ButtonPseudo
                      size={ButtonSize.SMALL}
                      text={
                        warningInGroupId === warning?.id &&
                        beneficiariesIsLoading
                          ? 'Loading...'
                          : 'Show More'
                      }
                      disabled={beneficiariesIsLoading}
                      onClick={() =>
                        getWarningGroupBeneficiaries({
                          id: groupId,
                          warningId: warning.id,
                          cursor: warning?.warningsToPaymentOrderBeneficiaries
                            ?.pageInfo?.endCursor as string,
                        })
                      }
                    />
                  </div>
                )}
            </>
          )}
        {warning?.action ? (
          <div
            className={styles.warningAction}
          >{`Action: ${getWarningActionName(warning?.action)}`}</div>
        ) : (
          ''
        )}
        {activePermissions?.includes(PermissionEnum.CHANNELS_LIST) &&
          activePermissions?.includes(PermissionEnum.CHANNELS_CREATE_ACTION) &&
          !warningsReport && (
            <RequestBlock
              setRequestText={setRequestText}
              selectedRequest={selectedRequest}
              requestText={requestText}
              setSelectedRequest={setSelectedRequest}
              action={warning?.action}
              internal={internal}
              companyOfficials={companyOfficials}
              recipients={recipients}
              senders={senders}
              individualCustomer={individualCustomer}
              recipientAccount={recipientAccount}
              senderAccount={senderAccount}
              chats={chats}
              createChatWithMessage={createChatWithMessage}
              chatCreateInProgress={chatCreateInProgress}
            />
          )}

        {onApplyTransactionAction && !warning?.action && (
          <div className={styles.actionWrapper}>
            <Select
              name="actions"
              value={action}
              onChange={(e) => setAction(e.target.value as string)}
              list={[
                {
                  name: 'Pass once',
                  value: TransactionWarningActionEnum.PASS_ONCE,
                  hidden: hideApproveActions,
                },
                {
                  name: 'Stop',
                  value: TransactionWarningActionEnum.STOP,
                  hidden: hideRejectActions,
                },
              ]}
              placeholder="Select actions"
              selectWrapperClassName={styles.selectAction}
              disabled={
                Boolean(warning?.action) || applyTransactionsWarningIsLoading
              }
              fullWidth
            />
          </div>
        )}
        {!warning?.action && !onApplyTransactionAction && (
          <div
            className={`${styles.warningActionApplied} ${styles.resolutionNotSelected}`}
          >
            Resolution not selected
          </div>
        )}
        {warning?.action && onResetTransactionWarningAction && (
          <ButtonSecondary
            text="Reset actions"
            icon={<IconRefresh />}
            size={ButtonSize.SMALL}
            className={styles.resetWarningStatus}
            onClick={() => setResetPopupVisible(true)}
          />
        )}
        {warning?.action &&
          warning?.employee &&
          warning?.employee?.profile?.firstName &&
          warning?.employee?.profile?.lastName && (
            <div className={styles.warningActionApplied}>
              <div className={styles.warningActionAppliedLeft}>
                Resolved by&nbsp;
                <span>{`${warning?.employee?.profile?.firstName} ${warning?.employee?.profile?.lastName}`}</span>
              </div>
              <span>{formatDate(warning?.updatedAt as string, true)}</span>
            </div>
          )}
        {action && (
          <Textarea
            name="flagText"
            value={warning?.action ? `«${flagText}»` : flagText}
            onChange={(e) => setFlagText(e.target.value)}
            placeholder="Enter a comment..."
            disabled={
              Boolean(warning?.action) || applyTransactionsWarningIsLoading
            }
            labelClassName={styles.flagTextareaLabel}
            className={warning?.action ? styles.flagTextareaDisabled : ''}
            borderClassName={
              warning?.action ? styles.flagTextareaBorderDisabled : ''
            }
          />
        )}
        <div
          className={`${styles.applyWarningWrapper} ${
            warning?.action ? styles.flexEnd : ''
          }`}
        >
          {!warning?.action && onApplyTransactionAction && (
            <ButtonPrimary
              text={
                applyTransactionsWarningIsLoading
                  ? 'Loading...'
                  : 'Apply action'
              }
              className={styles.applyActionButton}
              size={ButtonSize.SMALL}
              disabled={
                !(action && flagText) || applyTransactionsWarningIsLoading
              }
              onClick={onApply}
            />
          )}
          {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
            <NavLink
              to={`/administration/change-logs?entityType=WARNING&entityId=${warning?.id}`}
              className={styles.changeLogLink}
              target="_blank"
            >
              <div>
                <IconChangeLoClock />
                Change Log
              </div>
            </NavLink>
          )}
        </div>
        {warningActionError && warningActionError.warningId === warning?.id && (
          <div className={styles.warningActionError}>
            {warningActionError.error}
          </div>
        )}
      </>
    );
  }

  return (
    <div
      className={`${styles.warningItem} ${
        withWhiteBackground ? styles.whiteBackground : ''
      }`}
    >
      <button
        type="button"
        className={styles.warningItemCollapseButton}
        onClick={() => {
          setCollapsed(!collapsed);

          if (hideWarningsIds && setHideWarningsIds) {
            let nextHideWarningsIds = ([] as string[]).concat(hideWarningsIds);

            if (!collapsed) {
              nextHideWarningsIds = nextHideWarningsIds.filter(
                (item) => item !== warning?.id,
              );
            } else {
              nextHideWarningsIds.push(warning?.id as string);
            }

            setHideWarningsIds(nextHideWarningsIds);
          }

          if (getWarningById) {
            getWarningById({
              id: entityId as string,
              warningId: warning?.id as string,
            });
          }
        }}
      >
        <div
          className={`${styles.buttonLeft} ${styles.monitoringRuleButtonLeft}`}
        >
          {warningIcon}
          <div className={styles.monitoringRuleName}>
            {warning?.monitoringRule?.name}
          </div>
        </div>
        <div
          className={`${styles.buttonRight} ${styles.monitoringRuleButtonRight}`}
        >
          <div className={styles.buttonIconWrapper}>
            {collapsed ? <IconUp /> : <IconDown />}
          </div>
        </div>
      </button>
      {collapsed && collapsedContent}
      <Popup
        title="The selected actions will be canceled"
        onCancelClick={() => setResetPopupVisible(false)}
        onOkClick={() =>
          onResetTransactionWarningAction &&
          onResetTransactionWarningAction(warning?.id as string)
        }
        cancelButtonText="Cancel"
        okButtonText={
          resetTransactionsWarningIsLoading ? 'Loading...' : 'Yes, reset'
        }
        isVisible={resetPopupVisible}
        isLoading={resetTransactionsWarningIsLoading}
        parent={document.getElementById('signature-popup') as HTMLElement}
      />
    </div>
  );
}
